import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { ThemeProvider as MUIThemeProvider, createTheme, Theme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import api from '../services/authService';

interface ThemeContextType {
  theme: Theme;
  themeMode: 'light' | 'dark';
  setThemeMode: (mode: 'light' | 'dark') => void;
  appName: string;
  logo: string | null;
  favicon: string | null;
  themeColor: string;
   sidebarLightBg: string;
   sidebarLightText: string;
   sidebarDarkBg: string;
   sidebarDarkText: string;
   currentSidebarBg: string;
   currentSidebarText: string;
  refreshSettings: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within ThemeContextProvider');
  }
  return context;
};

interface ThemeContextProviderProps {
  children: ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  // Initialize from localStorage or default to 'dark'
  const [themeMode, setThemeModeState] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('themeMode');
    return (saved === 'light' || saved === 'dark') ? saved : 'dark';
  });
  const [appName, setAppName] = useState<string>('Smart Building Management');
  const [logo, setLogo] = useState<string | null>(null);
  const [favicon, setFavicon] = useState<string | null>(null);
  const [sidebarLightBg, setSidebarLightBg] = useState<string>('#ffffff');
  const [sidebarLightText, setSidebarLightText] = useState<string>('#000000');
  const [sidebarDarkBg, setSidebarDarkBg] = useState<string>('#121212');
  const [sidebarDarkText, setSidebarDarkText] = useState<string>('#ffffff');
  const [themeColor, setThemeColor] = useState<string>('#1976d2');

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings/app');
      const settings = response.data;
      
      if (settings.theme) {
        setThemeModeState(settings.theme);
        localStorage.setItem('themeMode', settings.theme);
      }
      if (settings.app_name !== undefined && settings.app_name !== null) {
        const name = settings.app_name || 'Smart Building';
        setAppName(name);
        updateSEOTags(name);
      }
      if (settings.logo) {
        // If logo is already a full URL, use it directly
        if (settings.logo.startsWith('http://') || settings.logo.startsWith('https://')) {
          setLogo(settings.logo);
        } else {
          // Otherwise, construct the full URL
          const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://bms.sarahkw.com/backend/public/api';
          const storageBase = apiBaseUrl.replace('/api', '');
          // Handle both /storage/... and storage/... paths
          const logoPath = settings.logo.startsWith('/') ? settings.logo : `/${settings.logo}`;
          setLogo(`${storageBase}${logoPath}`);
        }
      } else {
        setLogo(null);
      }
      if (settings.favicon) {
        // If favicon is already a full URL, use it directly
        if (settings.favicon.startsWith('http://') || settings.favicon.startsWith('https://')) {
          setFavicon(settings.favicon);
          updateFavicon(settings.favicon);
        } else {
          // Otherwise, construct the full URL
          const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://bms.sarahkw.com/backend/public/api';
          const storageBase = apiBaseUrl.replace('/api', '');
          const faviconPath = settings.favicon.startsWith('/') ? settings.favicon : `/${settings.favicon}`;
          const fullFaviconUrl = `${storageBase}${faviconPath}`;
          setFavicon(fullFaviconUrl);
          updateFavicon(fullFaviconUrl);
        }
      }

      if (settings.theme_color) {
        setThemeColor(settings.theme_color);
      }
      if (settings.sidebar_light_bg) {
        setSidebarLightBg(settings.sidebar_light_bg);
      }
      if (settings.sidebar_light_text) {
        setSidebarLightText(settings.sidebar_light_text);
      }
      if (settings.sidebar_dark_bg) {
        setSidebarDarkBg(settings.sidebar_dark_bg);
      }
      if (settings.sidebar_dark_text) {
        setSidebarDarkText(settings.sidebar_dark_text);
      }
    } catch (error) {
      console.error('Failed to load app settings:', error);
    }
  };

  const updateFavicon = (faviconPath: string | null) => {
    if (!faviconPath) return;
    
    let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'shortcut icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    
    link.type = 'image/x-icon';
    // If faviconPath is already a full URL, use it directly
    if (faviconPath.startsWith('http://') || faviconPath.startsWith('https://')) {
      link.href = faviconPath;
    } else {
      // Otherwise, construct the full URL
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://bms.sarahkw.com/backend/public/api';
      const storageBase = apiBaseUrl.replace('/api', '');
      const path = faviconPath.startsWith('/') ? faviconPath : `/storage/${faviconPath}`;
      link.href = `${storageBase}${path}`;
    }
  };

  const updateSEOTags = (appName: string) => {
    // Update document title
    document.title = appName;

    // Update or create meta tags for SEO
    const updateMetaTag = (name: string, content: string, attribute: string = 'name') => {
      let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.getElementsByTagName('head')[0].appendChild(meta);
      }
      meta.content = content;
    };

    // Standard meta tags
    updateMetaTag('title', appName);
    updateMetaTag('description', `${appName} - Building Management System`);
    updateMetaTag('application-name', appName);

    // Open Graph tags
    updateMetaTag('og:title', appName, 'property');
    updateMetaTag('og:description', `${appName} - Building Management System`, 'property');
    updateMetaTag('og:site_name', appName, 'property');

    // Twitter Card tags
    updateMetaTag('twitter:title', appName);
    updateMetaTag('twitter:description', `${appName} - Building Management System`);
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // Save theme mode to localStorage for persistence
  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
  }, [themeMode]);

  const setThemeMode = async (mode: 'light' | 'dark') => {
    try {
      const formData = new FormData();
      formData.append('theme', mode);
      await api.post('/settings/app', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setThemeModeState(mode);
      localStorage.setItem('themeMode', mode);
      // Force a re-render by updating state
    } catch (error) {
      console.error('Failed to update theme:', error);
      // Still update locally even if API call fails
      setThemeModeState(mode);
      localStorage.setItem('themeMode', mode);
    }
  };

  const theme = useMemo(() => createTheme({
    palette: {
      mode: themeMode,
      primary: {
        main: themeColor,
      },
      secondary: {
        main: '#dc004e',
      },
      background: {
        default: themeMode === 'dark' ? '#121212' : '#f5f5f5',
        paper: themeMode === 'dark' ? '#2C2C2C' : '#ffffff',
      },
    },
  }), [themeMode, themeColor]);

  const currentSidebarBg =
    themeMode === 'dark' ? sidebarDarkBg || '#121212' : sidebarLightBg || '#ffffff';
  const currentSidebarText =
    themeMode === 'dark' ? sidebarDarkText || '#ffffff' : sidebarLightText || '#000000';

  const value: ThemeContextType = {
    theme,
    themeMode,
    setThemeMode,
    appName,
    logo,
    favicon,
    themeColor,
    sidebarLightBg,
    sidebarLightText,
    sidebarDarkBg,
    sidebarDarkText,
    currentSidebarBg,
    currentSidebarText,
    refreshSettings: loadSettings,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};

